"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
export type SimpleProductKind = "course" | "digital_product" | "bundle" | "membership";
export interface SimpleProductInfo {
  id: string;
  title: string;
  price: number;
  compare_at_price: number | null;
  thumbnail_url: string | null;
  /** Hanya berarti untuk kind 'course' — 'free' vs berbayar. */
  access_type: string | null;
  sold_out: boolean;
  /** Hanya untuk kind 'membership' — 'monthly'|'yearly'|'lifetime'|dst, untuk label "per bulan"/dst. */
  duration_type: string | null;
}
const DURATION_LABEL: Record<string, string> = {
  monthly: "per bulan",
  yearly: "per tahun",
  lifetime: "sekali bayar, akses selamanya"
};
export function durationLabel(durationType: string | null): string | null {
  return durationType ? DURATION_LABEL[durationType] ?? null : null;
}

// API publik berbeda per jenis produk:
// - course: field dibungkus di bawah key `course` (bersama `modules` dll), pakai `title`.
// - digital_product/bundle: field rata di `data`, pakai `name`.
// - membership: /api/membership/plans mengembalikan ARRAY semua plan aktif
//   (tidak ada endpoint single-plan-by-slug) — cari manual by slug, sama
//   persis pola yang dipakai /order/[productSlug]/page-client.tsx.
async function fetchProduct(kind: SimpleProductKind, slug: string): Promise<SimpleProductInfo | null> {
  if (kind === "membership") {
    const res = await fetch("/api/membership/plans").then(r => r.json());
    if (!res.success || !Array.isArray(res.data)) return null;
    const plan = (res.data as Record<string, unknown>[]).find(p => p.slug === slug);
    if (!plan) return null;
    return {
      id: plan.id as string ?? "",
      title: plan.name as string ?? "",
      price: Number(plan.price ?? 0),
      compare_at_price: plan.compare_at_price != null ? Number(plan.compare_at_price) : null,
      thumbnail_url: null,
      access_type: null,
      sold_out: false,
      duration_type: plan.duration_type as string ?? null
    };
  }
  const endpoint = kind === "course" ? `/api/courses/${slug}` : kind === "digital_product" ? `/api/public/digital-products/${slug}` : `/api/public/product-bundles/${slug}`;
  const res = await fetch(endpoint).then(r => r.json());
  if (!res.success || !res.data) return null;
  const raw = res.data as Record<string, unknown>;
  const d = kind === "course" ? raw.course as Record<string, unknown> ?? raw : raw;
  return {
    id: d.id as string ?? "",
    title: d.title as string ?? d.name as string ?? "",
    price: Number(d.price ?? 0),
    compare_at_price: d.compare_at_price != null ? Number(d.compare_at_price) : null,
    thumbnail_url: d.thumbnail_url as string | null ?? null,
    access_type: d.access_type as string | null ?? null,
    sold_out: d.sold_out === true,
    duration_type: null
  };
}
export function useProductInfo(kind, slug) {
  const $ = _c(7);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  let t0;
  let t1;
  if ($[0] !== kind || $[1] !== slug) {
    t0 = () => {
      let alive = true;
      setLoading(true);
      if (!slug) {
        setProduct(null);
        setLoading(false);
        return;
      }
      fetchProduct(kind, slug).then(info => {
        if (alive) {
          setProduct(info);
        }
      }).catch(() => {
        if (alive) {
          setProduct(null);
        }
      }).finally(() => {
        if (alive) {
          setLoading(false);
        }
      });
      return () => {
        alive = false;
      };
    };
    t1 = [kind, slug];
    $[0] = kind;
    $[1] = slug;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== loading || $[5] !== product) {
    t2 = {
      product,
      loading
    };
    $[4] = loading;
    $[5] = product;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}